// The longest prod Atlas snapshot retention (yearly snapshot, kept 1 year).
export const BACKUP_RETENTION = '12 months';

export function ShopifyPrivacySection() {
  return (
    <section aria-labelledby="privacy-shopify">
      <h2 id="privacy-shopify">SHOPIFY STORES</h2>
      <p>
        This section applies when a Brand installs the Discovr AI app on its Shopify store. It
        describes what Discovr receives from Shopify, why, how long we keep it, and when we delete
        it.
      </p>

      <h3>What We Receive from Shopify</h3>
      <ul>
        <li>
          <strong>Store details:</strong> the store's Shopify address (for example,
          your-store.myshopify.com), its currency and time zone, the permissions the Brand granted,
          and the access tokens that let Discovr work with the store. We hold these while the app is
          installed. We also read the store's name to show it in Discovr; we cache it for about 60
          seconds and never store it.
        </li>
        <li>
          <strong>Products and collections:</strong> the names, links, images and IDs of the
          products and collections a Brand picks for a discount.
        </li>
        <li>
          <strong>Discount codes:</strong> the codes Discovr creates in the store for the Brand's
          campaigns, their settings, and Shopify's count of how often each code was used. Codes and
          tracking links are emailed to the creator they belong to, and can appear on campaign
          share links the Discovr team creates. Those links can be password-protected and expire,
          and show the store address, codes, discount terms and the creators they belong to, but
          no sales or usage figures.
        </li>
        <li>
          <strong>Orders and refunds:</strong> Shopify sends us every new or updated order in the
          store. As each one arrives we reduce it to the order number and ID, the discount codes
          used (any codes, not only Discovr's), the Discovr referral code in the link the shopper
          arrived from (if any), the order's total discount, currency, subtotal, tax on the items,
          payment status, refund and cancellation details and dates, and whether it was a Shopify
          test order, and queue it so we can check it for a Discovr code or Discovr link. This
          includes orders that did
          not use a Discovr code. Once an order is processed, we delete those order details and keep
          only its IDs, status and timestamps, for up to 30 days. Orders still waiting or being
          retried keep their order details until they're processed, and orders that finally fail
          processing keep them for 30 days; these never include the shopper's name, email address or
          postal address. When an order used one of the Brand's Discovr codes, or came through one
          of its creators' Discovr links, we keep a sales record: the order number, the discount
          code used, currency, subtotal, total discount, refunded amount, net sales, payment status
          and dates, whether it was a test order and whether it was credited by code or link,
          linked to the creator and campaign. We keep sales records until the app is uninstalled or
          an erasure request removes them. Other orders get no sales record. When an order used
          one of the Brand's Discovr codes or links but was not credited to a creator, we keep a
          short record of it (the order number, order value and currency, the code or link used,
          and why it was not credited) for up to 2 years, so the Brand and Discovr staff can see
          why. These records are deleted when the store uninstalls the app. We never store the
          shopper's name, email address, phone number or postal address.
        </li>
        <li>
          <strong>Discount changes:</strong> when a discount changes in Shopify, Shopify tells us.
          We store only the discount ID, title, status, timestamps and any removed code, in the same
          queue for up to 30 days after it is processed.
        </li>
        <li>
          <strong>Referral visits:</strong> Discovr's Shopify web pixel reports a page view when a
          shopper arrives on the store through a creator's Discovr link. The pixel's request carries
          the store's address, a key issued for that store, the creator tag from the link, an event
          ID, the event name and the time. It does not send the shopper's name, email address or
          cart. When the tag matches one of the Brand's Discovr codes, we keep a visit record: the
          event name, the creator, campaign and code (as internal IDs), the store, event ID and
          time. Otherwise nothing is kept.
        </li>
      </ul>

      <h3>How We Use It</h3>
      <p>
        We use this data to create and manage the discount codes a Brand asks for, and to show the
        Brand which creators' codes and links led to sales and visits in its campaigns. Sales are
        attributed through discount codes or a creator's Discovr link. Referral visits are approximate, because they depend on
        shopper consent and browser behavior. Orders placed in Shopify test mode are labelled as
        test orders in Discovr. Discovr never bills on Shopify orders, test or real; our fees are
        based on campaign budgets. Discovr staff can view a Brand's codes and sales to support it. We do
        not sell this data.
      </p>

      <h3>Service Providers</h3>
      <p>
        Shopify is the platform this store data comes from. These providers process it for us:
        Amazon Web Services (hosting), MongoDB Atlas (database), Resend (email delivery) and
        Microsoft Clarity (recordings of how the Discovr dashboard is used, with on-screen text
        masked).
      </p>

      <h3>Security</h3>
      <p>
        Data is encrypted in transit (TLS 1.2 or higher) and at rest (encrypted database and file
        storage). Shopify access tokens are additionally encrypted with AES-256 by our application.
      </p>

      <h3>How Long We Keep It While the App Is Installed</h3>
      <p>
        Shopify data (the store's address, order IDs, amounts, discount codes and dates, and pixel
        page views) is kept while the app is installed, unless a shorter period is listed below.
        Except as described under Shopper Privacy Requests and What We Keep After Uninstall, it is
        deleted when the app is uninstalled or when Shopify sends a redact request. Backup copies
        are covered under Backups below.
      </p>
      <ul>
        <li>Visit records are deleted automatically after 2 years.</li>
        <li>Sales records and discount code records are kept while the store stays installed.</li>
        <li>
          Activity log entries for connecting or uninstalling a store keep the store's address while
          the store stays installed.
        </li>
        <li>
          Short-lived sign-in and install records (the Discovr user, role and store address) expire
          on their own: install state within 30 minutes, a connect intent within 15 minutes and a
          claim link within 24 hours.
        </li>
      </ul>

      <h3>Shopper Consent</h3>
      <p>
        The web pixel uses Shopify's customer privacy settings. It asks for analytics and marketing
        (attribution) permission, not for sale of data, and it runs only when the visitor allows
        analytics and marketing (attribution) in the store's cookie and consent settings. Where
        consent is required and the shopper has not given it, nothing is sent. Like any request to
        our servers, the pixel's request includes the shopper's IP address. We do not write it to
        our application logs or traces. We use it in a rate-limit counter that expires after a
        minute or two, and our web firewall and load balancer keep security logs of incoming
        requests, including the IP address, for up to 30 days. It is not stored with the visit. Our
        application logs, which record the IP address of other requests to our servers, are kept for
        30 days.
      </p>

      <h3>Shopper Privacy Requests</h3>
      <p>Shopify passes shopper privacy requests to us. We only read the order IDs in them.</p>
      <ul>
        <li>
          <strong>Data request:</strong> we email the Brand's owners (or, if no owner can receive
          it, Discovr staff, who reply to the store) the order number, code, amounts, status and
          date of the sales records we hold for the orders named, so the store can answer the
          shopper. Once sent, that email is outside Discovr. We delete our copy of the email job 30
          days after sending.
        </li>
        <li>
          <strong>Erasure request:</strong> we delete the sales records for the orders named.
          Processing-queue copies of those orders expire within 30 days. So that a later update to
          the same order can't add it back, we keep only the store's address and the order ID, with
          no amounts or codes, until the store's data is erased or for up to 2 years.
        </li>
        <li>
          <strong>Store erasure:</strong> Shopify sends this about 48 hours after a store uninstalls
          the app. We delete anything we still hold for that store.
        </li>
      </ul>

      <h3>Uninstalling</h3>
      <p>
        A Brand can uninstall Discovr from Discovr (Integrations → Shopify → Connection settings →
        Uninstall Discovr from Shopify) or from Shopify admin. Either way, Discovr stops tracking.
        Access tokens are deleted as soon as we learn of the uninstall. The rest of the store's
        Shopify data (sales records, visit records, discount code records, product targeting, campaign
        Shopify settings and queued order updates) is deleted automatically, normally within
        minutes. If a deletion fails, it is retried and our team is alerted, and they complete the
        deletion within 30 days of the uninstall. If Discovr loses access to a store without being
        uninstalled, it stops processing and keeps the data for up to 7 days from when it detects
        the lost access, in case access returns. After that, the store is treated as uninstalled and
        its data is deleted.
      </p>
      <p>
        Discount codes stay in the Shopify store unless the Brand chooses "Turn off all Discovr
        discount codes in Shopify first" when uninstalling from Discovr, or deletes them in Shopify.
        Discovr can't change them after uninstall. The Brand's campaigns, creators and content in
        Discovr are not affected.
      </p>

      <h3>What We Keep After Uninstall</h3>
      <ul>
        <li>
          Activity log entries for the store, such as when it was connected or uninstalled and which
          Discovr user did it, with the store's address replaced by "[removed]". We delete these 90
          days after the uninstall.
        </li>
        <li>
          A note on the Brand's account and campaigns that their Shopify data was removed, with no
          store details. The account note is deleted after 90 days.
        </li>
        <li>
          Records of the privacy requests described above (the store's address and the order IDs
          named), kept for up to 30 days after they are processed; the data-request email job,
          deleted 30 days after the email is sent; and erasure records as described above.
        </li>
        <li>Server logs that can include the store's address, deleted after 30 days.</li>
      </ul>

      <h3>Backups</h3>
      <p>
        Copies of our database are kept in backups for up to {BACKUP_RETENTION} and then deleted
        automatically. Backups are only used to recover from a failure, never to run the service. If
        we ever restore a backup, we first delete again the data of every store uninstalled, and
        every order erased at a shopper's request, since that backup was taken.
      </p>
    </section>
  );
}
